import { useState, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { 
  ImagePlus, 
  X, 
  Loader2, 
  Sparkles, 
  ChevronDown,
  ChevronUp,
  Upload,
  Check,
  ZoomIn
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/hooks/use-i18n";
import { AnalyzedImage } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { captureEvent } from "@/lib/posthog";

interface ImageAnalysisResult {
  labels: string[];
  description: string;
  suggestedType: "hero" | "prize" | "trophy" | "unknown";
  confidence: number;
}

interface ImagePanelProps {
  images: AnalyzedImage[];
  onImagesChange: (images: AnalyzedImage[]) => void;
  onGenerateImage?: (prompt: string, type: "hero" | "prize" | "trophy") => Promise<string>;
  onAddToPage?: () => void;
  isGenerating?: boolean;
  isAddingToPage?: boolean;
  hasExistingPage?: boolean;
}

export function ImagePanel({ 
  images, 
  onImagesChange, 
  onGenerateImage,
  onAddToPage,
  isGenerating = false,
  isAddingToPage = false,
  hasExistingPage = false,
}: ImagePanelProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [previewImage, setPreviewImage] = useState<AnalyzedImage | null>(null);
  const { toast } = useToast();
  const { t } = useI18n();

  const analyzeImageMutation = useMutation({
    mutationFn: async ({ imageUrl }: { imageUrl: string }) => {
      const response = await apiRequest("POST", "/api/analyze-image", { imageUrl });
      return response.json() as Promise<{ analysis: ImageAnalysisResult }>;
    },
  });

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const readFileAsDataUrl = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFiles = useCallback(async (files: File[]) => {
    const validFiles = files.filter((file) => {
      if (!file.type.startsWith("image/")) {
        toast({
          title: t("invalidFileType"),
          description: `${file.name} ${t("isNotImageFile")}`,
          variant: "destructive",
        });
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: t("fileTooLarge"),
          description: `${file.name} ${t("isLargerThan10MB")}`,
          variant: "destructive",
        });
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    const newImages: AnalyzedImage[] = await Promise.all(
      validFiles.map(async (file) => {
        const id = crypto.randomUUID();
        const dataUrl = await readFileAsDataUrl(file);
        return {
          id,
          url: dataUrl,
          filename: file.name,
          isAnalyzing: true,
          uploadStatus: "analyzing" as const,
          uploadProgress: 50,
        };
      })
    );

    const allImages = [...images, ...newImages];
    onImagesChange(allImages);

    const processFile = async (file: File, imageData: AnalyzedImage) => {
      try {
        const uploadUrlRes = await fetch("/api/objects/upload", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });

        let finalUrl = imageData.url;

        if (uploadUrlRes.ok) {
          const { uploadURL } = await uploadUrlRes.json();
          
          const uploadRes = await fetch(uploadURL, {
            method: "PUT",
            body: file,
            headers: { "Content-Type": file.type },
          });

          if (uploadRes.ok) {
            const updateRes = await fetch("/api/images", {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ imageURL: uploadURL }),
            });

            if (updateRes.ok) {
              const { objectPath } = await updateRes.json();
              finalUrl = window.location.origin + objectPath;
            }
          }
        }

        const analysisResult = await analyzeImageMutation.mutateAsync({
          imageUrl: finalUrl,
        });

        return {
          ...imageData,
          url: finalUrl,
          isAnalyzing: false,
          uploadStatus: "matched" as const,
          uploadProgress: 100,
          analysis: analysisResult.analysis,
        };
      } catch (error) {
        console.error("Image processing error:", error);
        return {
          ...imageData,
          isAnalyzing: false,
          uploadStatus: "error" as const,
          errorMessage: t("analysisFailedMessage"),
        };
      }
    };

    const results = await Promise.all(
      validFiles.map((file, index) => processFile(file, newImages[index]))
    );

    const finalImages = images.concat(results);
    onImagesChange(finalImages);

    const successCount = results.filter(r => r.uploadStatus === "matched").length;
    if (successCount > 0) {
      captureEvent("images_uploaded", {
        count: successCount,
        total_attempted: validFiles.length,
        image_types: validFiles.map(f => f.type),
      });
      toast({
        title: `${successCount} ${t("imagesAnalyzed")}`,
        description: t("readyToAdd"),
      });
    }
  }, [images, onImagesChange, toast, analyzeImageMutation, t]);

  const handleGenerateAI = async () => {
    if (!aiPrompt.trim() || !onGenerateImage) return;

    captureEvent("ai_image_generation_started", {
      prompt_length: aiPrompt.trim().length,
      image_type: "prize",
    });

    try {
      const imageUrl = await onGenerateImage(aiPrompt.trim(), "prize");
      
      const id = crypto.randomUUID();
      const newImage: AnalyzedImage = {
        id,
        url: imageUrl,
        filename: t("aiGeneratedFilename"),
        isAnalyzing: true,
        uploadStatus: "analyzing",
        uploadProgress: 50,
      };

      onImagesChange([...images, newImage]);
      setAiPrompt("");

      const analysisResult = await analyzeImageMutation.mutateAsync({
        imageUrl: imageUrl,
      });

      onImagesChange(
        [...images, newImage].map((img) =>
          img.id === id
            ? {
                ...img,
                isAnalyzing: false,
                uploadStatus: "matched" as const,
                uploadProgress: 100,
                analysis: analysisResult.analysis,
              }
            : img
        )
      );

      captureEvent("ai_image_generated", {
        image_type: analysisResult.analysis.suggestedType,
      });
      toast({
        title: t("imageGenerated"),
        description: analysisResult.analysis.description,
      });
    } catch (error) {
      captureEvent("ai_image_generation_failed");
      console.error("Generation error:", error);
      toast({
        title: t("generationFailedShort"),
        description: t("couldNotGenerateImage"),
        variant: "destructive",
      });
    }
  };

  const removeImage = (id: string) => {
    captureEvent("image_removed");
    onImagesChange(images.filter((img) => img.id !== id));
  };

  const imageCount = images.length;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border-b">
      <CollapsibleTrigger asChild>
        <button
          className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium hover-elevate"
          data-testid="button-toggle-images"
        >
          <div className="flex items-center gap-2">
            <ImagePlus className="w-4 h-4 text-muted-foreground" />
            <span>{t("prizeImages")}</span>
            {imageCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {imageCount}
              </Badge>
            )}
          </div>
          {isOpen ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="px-3 sm:px-4 pb-3 sm:pb-4 space-y-2 sm:space-y-3">
          <div
            className={`relative border-2 border-dashed rounded-lg p-3 sm:p-4 transition-colors ${
              dragActive
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25 hover:border-muted-foreground/50"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            data-testid="dropzone-images"
          >
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileInput}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              data-testid="input-file-upload"
            />
            <div className="flex flex-col items-center gap-1 text-center pointer-events-none">
              <Upload className="w-5 h-5 sm:w-6 sm:h-6 text-muted-foreground" />
              <p className="text-[10px] sm:text-xs text-muted-foreground">
                {t("dropImagesOrClick")}
              </p>
            </div>
          </div>

          {onGenerateImage && (
            <div className="flex gap-1.5 sm:gap-2">
              <Input
                placeholder={t("generateWithAI")}
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleGenerateAI()}
                className="text-xs sm:text-sm h-7 sm:h-8"
                disabled={isGenerating}
                data-testid="input-ai-prompt"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleGenerateAI}
                disabled={!aiPrompt.trim() || isGenerating}
                className="h-7 sm:h-8 px-2"
                data-testid="button-generate-ai"
              >
                {isGenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
              </Button>
            </div>
          )}

          {images.length > 0 && (
            <ScrollArea className="max-h-24 sm:max-h-32">
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {images.map((image) => (
                  <div
                    key={image.id}
                    className="relative group"
                    data-testid={`image-thumbnail-${image.id}`}
                  >
                    <div 
                      className="relative w-11 h-11 sm:w-14 sm:h-14 rounded-md overflow-hidden border bg-muted cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
                      onClick={() => !image.isAnalyzing && setPreviewImage(image)}
                      data-testid={`button-preview-image-${image.id}`}
                    >
                      <img
                        src={image.url}
                        alt={image.filename}
                        className="w-full h-full object-cover"
                      />
                      {image.isAnalyzing && (
                        <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                          <Loader2 className="w-4 h-4 animate-spin text-primary" />
                        </div>
                      )}
                      {!image.isAnalyzing && (
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-colors">
                          <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      )}
                      {image.uploadStatus === "matched" && (
                        <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-tl flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage(image.id);
                        }}
                        className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`button-remove-image-${image.id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    {image.analysis && (
                      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 max-w-20">
                        <Badge variant="outline" className="text-[9px] px-1 py-0 truncate bg-background">
                          {image.analysis.labels[0] || image.analysis.suggestedType}
                        </Badge>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}

          {images.length > 0 && hasExistingPage && onAddToPage && (
            <Button
              onClick={() => {
                captureEvent("images_added_to_page", { image_count: images.length });
                onAddToPage();
              }}
              disabled={isAddingToPage || images.some(img => img.isAnalyzing)}
              className="w-full"
              size="sm"
              data-testid="button-add-images-to-page"
            >
              {isAddingToPage ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("addingToPage")}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {t("addImagesToPage")}
                </>
              )}
            </Button>
          )}

          {images.length > 0 && !hasExistingPage && (
            <p className="text-xs text-muted-foreground text-center">
              {t("generateAwardsFirst")}
            </p>
          )}
        </div>
      </CollapsibleContent>

      <Dialog open={!!previewImage} onOpenChange={(open) => !open && setPreviewImage(null)}>
        <DialogContent className="max-w-2xl p-2">
          <DialogTitle className="sr-only">
            {previewImage?.filename || "Image Preview"}
          </DialogTitle>
          {previewImage && (
            <div className="space-y-3">
              <div className="relative bg-muted rounded-lg overflow-hidden flex items-center justify-center min-h-[300px]">
                <img
                  src={previewImage.url}
                  alt={previewImage.filename}
                  className="max-w-full max-h-[60vh] object-contain"
                  data-testid="img-preview-full"
                />
              </div>
              {previewImage.analysis && (
                <div className="space-y-2 px-2 pb-2">
                  <div className="flex flex-wrap gap-1">
                    {previewImage.analysis.labels.map((label, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {label}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {previewImage.analysis.description}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Collapsible>
  );
}
